// apps/cli/src/lib/bootstrap-state.ts
import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";

/** Local record of an in-flight / completed bootstrap. ~/.m8t/bootstrap.json */
export interface BootstrapState {
  subscriptionId: string;
  resourceGroup: string;
  location: string;
  aciName: string;
  miName: string;
  miClientId: string;
  miPrincipalId: string;
  /** ARM ids of the MI's role assignments, captured at launch for robust reaping. */
  roleAssignmentIds: string[];
  appRegClientId: string;
  /** Consumers (status/reap) re-derive the SA name; this is the read key. */
  statusSaName: string;
  /** Write-only diagnostics for humans inspecting the file — not read back. */
  statusBlobUrl: string;
  installerTag: string;
  /**
   * The install id this launch minted, so later local commands (`status`,
   * `profile`) can stamp their events with the same id the container reports to
   * the wire. Optional: a state file written by an older CLI has none, and a
   * consumer must tolerate that rather than refuse to read the install.
   */
  installId?: string;
}

function stateDir(home: string): string { return path.join(home, ".m8t"); }
function statePath(home: string): string { return path.join(stateDir(home), "bootstrap.json"); }

export async function readBootstrapState(home: string = os.homedir()): Promise<BootstrapState | null> {
  try {
    const raw = await fs.readFile(statePath(home), "utf8");
    return JSON.parse(raw) as BootstrapState;
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw e;
  }
}

export async function writeBootstrapState(state: BootstrapState, home: string = os.homedir()): Promise<void> {
  await fs.mkdir(stateDir(home), { recursive: true });
  const tmp = `${statePath(home)}.tmp`;
  await fs.writeFile(tmp, JSON.stringify(state, null, 2), "utf8");
  await fs.rename(tmp, statePath(home));
}
